import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Shield, LogOut, Users, Briefcase, DollarSign, CreditCard, Activity,
  MessageSquareHeart, RefreshCw, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabaseClient";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import HqFooter from "@/components/admin/HqFooter";

/**
 * Dedicated admin chrome — replaces the chef Navigation for `/hq-command-77`.
 * Renders header (identity + logout), a tab strip, and the active tab's body.
 *
 * Tabs are URL-keyed via `?tab=customers|team|revenue|billing|ops`.
 */
const TABS = [
  { key: "pulse",          label: "Pulse",            icon: Activity },
  { key: "customers",      label: "Customers",        icon: Users },
  { key: "team",           label: "PlateProfit Team", icon: Briefcase },
  { key: "customer-voice", label: "Customer Voice",   icon: MessageSquareHeart },
  { key: "revenue",        label: "Revenue",          icon: DollarSign },
  { key: "billing",        label: "Billing",          icon: CreditCard },
  { key: "ops",            label: "Operations",       icon: Activity },
];

export default function AdminLayout({ activeTab, children, onRefresh, refreshing }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        navigate("/hq-command-77");
        return;
      }
      const res = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      setMe(res.data);
    } catch {
      navigate("/hq-command-77");
    } finally {
      setLoadingMe(false);
    }
  }, [navigate]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const logout = async () => {
    await supabase.auth.signOut();
    navigate("/hq-command-77");
  };

  const tabHref = (key) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", key);
    return `/hq-command-77/users?${params.toString()}`;
  };

  if (loadingMe) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex flex-col" data-testid="hq-admin-layout">
      {/* Header */}
      <header className="border-b border-emerald-500/20 bg-gradient-to-r from-slate-950 to-slate-900 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-emerald-400" />
            <div>
              <h1 className="text-base font-bold text-white tracking-wide">
                HQ Command · Operator Console
              </h1>
              <p className="text-[0.65rem] uppercase tracking-[0.22em] text-emerald-400/70 font-mono">
                {me ? `${me.email || me.user_id} · ${me.role}` : "loading…"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
                data-testid="hq-admin-refresh"
              >
                <RefreshCw className={cn("h-3.5 w-3.5 mr-1.5", refreshing && "animate-spin")} />
                Refresh
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-slate-400 hover:text-white"
              data-testid="hq-admin-logout"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Sign out
            </Button>
          </div>
        </div>

        {/* Tab strip */}
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-1 border-t border-slate-800/60">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === activeTab;
            return (
              <Link
                key={tab.key}
                to={tabHref(tab.key)}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-xs font-semibold tracking-wide transition-colors border-b-2",
                  isActive
                    ? "border-emerald-400 text-emerald-300"
                    : "border-transparent text-slate-500 hover:text-slate-200 hover:border-slate-700"
                )}
                data-testid={`hq-admin-tab-${tab.key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </Link>
            );
          })}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-4 w-full flex-1">
        {typeof children === "function" ? children({ me }) : children}
      </main>

      <HqFooter />
    </div>
  );
}
